import { FC, useEffect, useState } from "react";
import {
  CssBaseline,
  Box,
  Grid,
  Typography,
  createTheme,
  ThemeProvider,
  Divider,
} from "@mui/material";
import { NameLogoBlue } from "../assets";
import {
  AuthOnboardingSlider,
  CommonButton,
  CommonLabel,
  CommonTextField,
  GoogleButton,
  CommonAlert,
  CommonLoading,
  CommonCheckbox,
  AppleOAuthButton,
  TextInputWithRegex,
} from "../components";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import {
  googleSignInFetchUserFromToken,
  guidGenerator,
  isUserInTurkey,
} from "../utils/commonFuncs";
import { IAppleOAuthCallbackCredentials } from "../utils/commonModels";
import jwt_decode from "jwt-decode";
// import { RegisterDevicePlatform, UserService } from "../client";
import { StorageAccessToken } from "../utils/constants";
import { registerReq } from "../apiClient/services/authService";

const theme = createTheme();

const Register: FC = () => {
  let navigate = useNavigate();

  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isUserLocatedInTurkey, setIsUserLocatedInTurkey] =
    useState<boolean>(false);

  const access_token = localStorage.getItem(StorageAccessToken);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalLoading(true);
    try {
      const data = new FormData(event.currentTarget);
      const response = await registerReq(data.get("username")?.toString().includes("@")
        ? (data.get("username") as string)
        : "",
        data.get("username")?.toString().includes("@")
          ? ""
          : data.get("username") as string,
        data.get('password') as string
      )

      localStorage.setItem(StorageAccessToken, response.data.accessToken)
      navigate("/code")

    } catch (error) {
      console.log("error", error)
      setError("Kullanıcı Kayıtlı.")
    }
    setLocalLoading(false)
  };

  useEffect(() => {
    setError(null);
  }, []);

  useEffect(() => {
    const fetchCountry = async () => {
      setLocalLoading(true);
      const locationIndex = await isUserInTurkey();
      setIsUserLocatedInTurkey(locationIndex);
      setLocalLoading(false);
    };

    fetchCountry();
  }, []);

  const registerWithGoogleFunc = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setError(null);
        const userFromToken = await googleSignInFetchUserFromToken(
          tokenResponse.access_token
        );
        if (userFromToken.email) {
          const response = await registerReq(userFromToken.email, "", guidGenerator())
          localStorage.setItem(StorageAccessToken, response.data.accessToken)
          navigate("/code")
        } else {
          setError(
            "Beklenmedik bir hata oluştu. Lütfen bizimle iletişime geçtin."
          );
        }
        return;
      } catch (error) {
        setError(
          "Beklenmedik bir hata oluştu. Lütfen bizimle iletişime geçtin."
        );
      }
    },
    onError: (error) => setError(error.error_description as string),
  });

  const registerWithAppleFunc = async (
    appleCredential: IAppleOAuthCallbackCredentials
  ) => {
    try {
      setError(null);
      const decodedTokenData = jwt_decode(appleCredential.id_token) as any;

      if (decodedTokenData?.email == null) {
        setError(
          "Beklenmedik bir hata oluştu. Lütfen bizimle iletişime geçtin."
        );
      } else {
        const response = await registerReq(decodedTokenData.email, "", guidGenerator())
        localStorage.setItem(StorageAccessToken, response.data.accessToken)
        navigate("/code")
      }
    } catch (error: any) {
      console.log("error in apple register: ", error);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CommonLoading loading={localLoading} />
      <CommonAlert
        _type={"error"}
        _open={error != null ? true : false}
        _onClose={() => setError(null)}
        _message={"Kullanıcı Kayıtlı."}
      />
      <Grid
        container
        component="main"
        sx={{ height: "100vh" }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={false}
          md={6}
          sx={{
            backgroundColor: { md: "#2574FF" },
            display: { xs: "none", sm: "none", md: "flex" },
          }}
        >
          <AuthOnboardingSlider />
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <Box
            sx={{
              my: 4,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingLeft: { md: 7, lg: 7 },
              paddingRight: { md: 7, lg: 7 },
            }}
          >
            <img src={NameLogoBlue} alt="" />
            <Typography
              style={{
                fontFamily: "Montserrat",
                fontWeight: "700",
                fontSize: "32px",
                lineHeight: "40px",
                color: "#171725",
                marginTop: "6%",
              }}
            >
              Merhaba 👋
            </Typography>
            <Typography
              style={{
                fontFamily: "Montserrat",
                fontWeight: "700",
                fontSize: "32px",
                lineHeight: "40px",
                color: "#171725",
                marginBottom: "5%",
              }}
            >
              ParentWiser Hesabını Oluştur!
            </Typography>

            <GoogleButton
              text="Google ile üye ol"
              _onClick={() => registerWithGoogleFunc()}
              _marginBottom="24px"
            />
            <AppleOAuthButton
              text="Apple ile üye ol"
              _onClick={registerWithAppleFunc}
            />
            <Divider
              style={{ width: "100%", marginTop: "5%", marginBottom: "4%" }}
            >
              <Typography
                style={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "22px",
                  color: "#78828A",
                }}
              >
                Veya {isUserLocatedInTurkey ? "telefon numaran" : "email"} ile üye ol
              </Typography>
            </Divider>
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <div style={{ textAlign: "start", width: "100%" }}>
                <CommonLabel
                  label={isUserLocatedInTurkey ? "Cep Telefonu Numaran" : "Email"}
                />
                <TextInputWithRegex
                  _placeHolder={
                    isUserLocatedInTurkey ? "Cep Telefonu Numaranı Yaz" : "Email girin"
                  }
                  formName="username"
                  _type={isUserLocatedInTurkey ? "number" : "text"}
                />
              </div>
              <div
                style={{ textAlign: "start", width: "100%", marginTop: "3%" }}
              >
                <CommonLabel label="Şifreni Belirle" />
                <CommonTextField
                  _placeHolder="Şifreni Belirle"
                  _type="password"
                  formName="password"
                />
              </div>
              <div
                style={{
                  textAlign: "start",
                  marginTop: "4%",
                  marginBottom: "2%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CommonCheckbox _required={true} />
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#2574FF",
                    marginLeft: "5px",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    window.open(`https://app.parentwiser.com/official-document/user_agreement`, "_blank")
                  }
                >
                  Kullanıcı Sözleşmesi
                </Typography>
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#9393AA",
                    marginLeft: "3px",
                    marginRight: "3px",
                  }}
                >
                  ve
                </Typography>
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#2574FF",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    window.open(`https://app.parentwiser.com/official-document/kvkk`, "_blank")
                  }
                >
                  KVKK Aydınlatma Metni
                </Typography>
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#9393AA",
                  }}
                >
                  ’ni onaylıyorum.
                </Typography>
              </div>
              <div
                style={{
                  textAlign: "start",
                  marginBottom: "4%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CommonCheckbox />
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#2574FF",
                    marginLeft: "5px",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    window.open(
                      `https://app.parentwiser.com/official-document/communication_permission`,
                      "_blank"
                    )
                  }
                >
                  İletişim İzni
                </Typography>
                <Typography
                  style={{
                    fontFamily: "Montserrat",
                    fontWeight: "400",
                    fontSize: "11px",
                    lineHeight: "16px",
                    color: "#9393AA",
                  }}
                >
                  ’ni okudum/ kabul ediyorum.
                </Typography>
              </div>
              <CommonButton text="Hesap Oluştur" />
            </form>
            <div style={{ display: "flex", marginTop: "4%" }}>
              <Typography
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: "600",
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#9CA4AB",
                }}
              >
                Hesabın var mı?
              </Typography>
              <Typography
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: "600",
                  fontSize: "16px",
                  lineHeight: "24px",
                  color: "#2574FF",
                  marginLeft: "2px",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/login")}
              >
                Giriş Yap
              </Typography>
            </div>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default Register;
